import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

interface InviteDetails {
  status: string;
  project_id: string;
  project_name: string;
  company_id: string;
  company_name: string;
  company_code: string;
  email: string;
  role: string;
  expires_at: string;
  invited_by_name: string;
}

type PageState =
  | 'loading'
  | 'not_found'
  | 'already_accepted'
  | 'expired'
  | 'needs_login'
  | 'needs_company'
  | 'ready'
  | 'joining_company'
  | 'accepting'
  | 'success';

const ROLE_COLOR: Record<string, string> = {
  developer: '#3b82f6',
  tester:    '#f59e0b',
  viewer:    '#6b7280',
};

export default function AcceptProjectInvite() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token') ?? '';
  const { user, loading: authLoading, memberships, joinCompany } = useAuth();
  const { showToast }   = useToast();
  const navigate        = useNavigate();

  const [invite,    setInvite]    = useState<InviteDetails | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [error,     setError]     = useState('');

  // Load invite details
  useEffect(() => {
    if (!token) { setPageState('not_found'); return; }

    api.get<InviteDetails>(`/api/projects/${projectId}/accept-invite?token=${token}`)
      .then(data => {
        setInvite(data);
      })
      .catch(() => setPageState('not_found'));
  }, [token, projectId]);

  // Determine page state once we have both invite + auth info
  useEffect(() => {
    if (authLoading || !invite) return;

    if (invite.status === 'accepted') { setPageState('already_accepted'); return; }
    if (invite.status === 'expired' || new Date(invite.expires_at) < new Date()) {
      setPageState('expired'); return;
    }

    if (!user) { setPageState('needs_login'); return; }

    const inCompany = memberships.some((m) => m.company_id === invite.company_id);
    if (!inCompany) { setPageState('needs_company'); return; }

    setPageState('ready');
  }, [authLoading, invite, user, memberships]);

  const handleJoinCompany = async () => {
    if (!invite) return;
    setPageState('joining_company');

    const { error: joinErr } = await joinCompany(invite.company_code, invite.role as 'developer' | 'tester');
    if (joinErr) {
      setError(joinErr.message || 'Failed to join company.');
      setPageState('needs_company');
      return;
    }

    handleAccept();
  };

  const handleAccept = async () => {
    setPageState('accepting');
    setError('');

    try {
      await api.post(`/api/projects/${projectId}/accept-invite`, { token });
      showToast("You've joined the project!", 'success');
      setPageState('success');
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation.');
      setPageState('ready');
    }
  };

  const returnUrl = `/projects/${projectId}/accept-invite?token=${token}`;
  const loginUrl  = `/login?redirect=${encodeURIComponent(returnUrl)}`;
  const signupUrl = invite
    ? `/signup?email=${encodeURIComponent(invite.email)}&companyCode=${invite.company_code}&role=${invite.role}&companyName=${encodeURIComponent(invite.company_name)}`
    : '/signup';

  if (pageState === 'loading' || authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center', padding: 56 }}>
          <div className="app-loading-spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (pageState === 'not_found') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#fef2f2' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h1 className="auth-title">Invitation Not Found</h1>
          <p className="auth-subtitle">This invite link is invalid or has been removed.</p>
          <button className="btn btn-primary btn-full" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (pageState === 'already_accepted') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#eff6ff' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1 className="auth-title">Already Accepted</h1>
          <p className="auth-subtitle">This invitation has already been used.</p>
          {invite && (
            <button className="btn btn-primary btn-full" style={{ marginTop: 24 }} onClick={() => navigate(`/projects/${invite.project_id}`)}>
              View Project →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (pageState === 'expired') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#fffbeb' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h1 className="auth-title">Invitation Expired</h1>
          <p className="auth-subtitle">This invite link expired after 7 days. Ask the project admin to send a new invitation.</p>
          <button className="btn btn-secondary btn-full" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (pageState === 'success' && invite) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#f0fdf7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#1C9B82" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1 className="auth-title">You're on the team!</h1>
          <p className="auth-subtitle">
            Welcome to <strong>{invite.project_name}</strong>.<br />
            You've joined as a <span style={{ color: ROLE_COLOR[invite.role] ?? '#6b7280', fontWeight: 600 }}>{invite.role}</span>.
          </p>
          <button className="btn btn-primary btn-full" style={{ marginTop: 24 }} onClick={() => navigate(`/projects/${invite.project_id}`)}>
            Open Project →
          </button>
        </div>
      </div>
    );
  }

  const InviteCard = () => (
    invite ? (
      <div className="invite-project-card">
        <div className="invite-project-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
        </div>
        <div className="invite-project-meta">
          <div className="invite-project-name">{invite.project_name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span className="dropdown-role-badge" style={{ background: ROLE_COLOR[invite.role] ?? '#6b7280' }}>
              {invite.role}
            </span>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{invite.company_name}</span>
          </div>
        </div>
      </div>
    ) : null
  );

  if (pageState === 'needs_login') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
            </svg>
          </div>
          <h1 className="auth-title">Project Invitation</h1>
          <p className="auth-subtitle">
            <strong>{invite?.invited_by_name}</strong> has invited you to collaborate on:
          </p>
          <InviteCard />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
            <Link to={loginUrl} className="btn btn-primary btn-full" style={{ textAlign: 'center', textDecoration: 'none' }}>
              Sign in to accept
            </Link>
            <div className="auth-divider"><span>or</span></div>
            <Link to={signupUrl} className="btn btn-full" style={{ textAlign: 'center', textDecoration: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)', background: 'none' }}>
              Create a new account
            </Link>
          </div>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 20 }}>
            Already have an account? Sign in above to accept without creating a new one.
          </p>
        </div>
      </div>
    );
  }

  if (pageState === 'needs_company' || pageState === 'joining_company') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <h1 className="auth-title">One more step</h1>
          <p className="auth-subtitle">
            To join this project you first need to join <strong>{invite?.company_name}</strong>.
          </p>
          <InviteCard />
          {error && (
            <div className="invite-feedback error" style={{ marginTop: 12 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {error}
            </div>
          )}
          <button className="btn btn-primary btn-full" style={{ marginTop: 20 }} onClick={handleJoinCompany} disabled={pageState === 'joining_company'}>
            {pageState === 'joining_company' ? <><span className="btn-spinner" /> Joining…</> : `Join ${invite?.company_name} & Accept`}
          </button>
          <button className="btn btn-full" style={{ marginTop: 10, background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} onClick={() => navigate('/')}>
            Decline
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
          </svg>
        </div>
        <h1 className="auth-title">Project Invitation</h1>
        <p className="auth-subtitle">
          <strong>{invite?.invited_by_name}</strong> has invited you to collaborate on:
        </p>
        <InviteCard />
        {invite?.email && (
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 8 }}>
            Invite sent to <strong>{invite.email}</strong>
          </p>
        )}
        {error && (
          <div className="invite-feedback error" style={{ marginTop: 12 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}
        <button className="btn btn-primary btn-full" style={{ marginTop: 20 }} onClick={handleAccept} disabled={pageState === 'accepting'}>
          {pageState === 'accepting' ? <><span className="btn-spinner" /> Accepting…</> : 'Accept Invitation'}
        </button>
        <button className="btn btn-full" style={{ marginTop: 10, background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} onClick={() => navigate('/')}>
          Decline
        </button>
      </div>
    </div>
  );
}
